import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { LogIn, ArrowLeft, Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";

function formatErr(detail) {
  if (!detail) return "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Login() {
  const { user, emailLogin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "";

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    if (user) {
      if (next && next.startsWith("/")) navigate(next, { replace: true });
      else navigate(user.is_admin ? "/admin" : "/my-bookings", { replace: true });
    }
  }, [user, navigate, next]);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + (next ? `?next=${encodeURIComponent(next)}` : "");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await emailLogin(email.trim(), password);
      toast.success("Berhasil masuk");
    } catch (e2) {
      const msg = formatErr(e2?.response?.data?.detail);
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-muted/40" data-testid="login-page">
      <Helmet><title>Masuk — BATIK Tour & Travel</title></Helmet>
      <div className="w-full max-w-md bg-card border border-border rounded-lg p-8 lg:p-10 shadow-xl">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-xs mb-6" data-testid="back-home">
          <ArrowLeft className="h-3.5 w-3.5" /> Kembali ke website
        </Link>

        <div className="h-12 w-12 rounded-full bg-accent/15 flex items-center justify-center mb-5">
          <LogIn className="h-5 w-5 text-accent" />
        </div>

        <p className="overline mb-2">Masuk Portal Jamaah</p>
        <h1 className="font-serif text-3xl text-primary tracking-tight leading-tight mb-6">
          Selamat datang kembali
        </h1>

        <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</label>
            <div className="mt-1.5 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="anda@email.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password</label>
              <Link to="/forgot-password" className="text-xs text-accent hover:underline" data-testid="forgot-link">Lupa password?</Link>
            </div>
            <div className="mt-1.5 relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                data-testid="login-password"
                type={showPw ? "text" : "password"}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Minimal 8 karakter"
                autoComplete="current-password"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {err && <p className="text-xs text-red-600" data-testid="login-error">{err}</p>}

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition-all"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Masuk
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">atau</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <button
          onClick={handleGoogle}
          data-testid="google-login-btn"
          className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-white border-2 border-border hover:border-primary text-primary py-2.5 text-sm font-medium transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Belum punya akun?{" "}
          <Link to="/register" className="text-accent font-semibold hover:underline" data-testid="to-register-link">
            Daftar sekarang
          </Link>
        </p>
      </div>
    </div>
  );
}
